import React from 'react';
import { CheckCircle2, Loader2 } from 'lucide-react';

type SaveProgressProps = {
	stage?: string;
	visible?: boolean;
};

const stageToStepKey = (stage?: string): 'save' | 'waiting' | 'api' | 'done' | null => {
	if (!stage) return null;
	if (stage.startsWith('save:')) return 'save';
	if (stage === 'ui:waiting') return 'waiting';
	if (stage.startsWith('api:')) return 'api';
	if (stage === 'ui:done' || stage === 'done') return 'done';
	return null;
};

const steps: Array<{ key: 'save' | 'waiting' | 'api' | 'done'; label: string; emoji: string }> = [
	{ key: 'save', label: 'Salvando arquivo', emoji: '💾' },
	{ key: 'waiting', label: 'Aguardando API', emoji: '⏳' },
	{ key: 'api', label: 'Confirmando na API', emoji: '✅' },
	{ key: 'done', label: 'Concluído!', emoji: '🎉' },
];

export function SaveProgress({ stage, visible = true }: SaveProgressProps) {
	if (!visible) return null;
	const current = stageToStepKey(stage);
	const currentIndex = current ? steps.findIndex(s => s.key === current) : -1;
	const isComplete = current === 'done';

	return (
		<div className={`p-4 rounded-lg border transition-all duration-300 ${
			isComplete 
				? 'bg-green-900/20 border-green-500/50 animate-pulse' 
				: 'bg-navy-800/70 border-navy-700'
		}`}>
			{/* Header com ícone animado */}
			<div className="flex items-center gap-2 mb-3">
				{isComplete ? (
					<CheckCircle2 className="w-5 h-5 text-green-400 animate-bounce" />
				) : (
					<Loader2 className="w-5 h-5 text-blue-400 animate-spin" />
				)}
				<p className={`text-sm font-medium ${isComplete ? 'text-green-300' : 'text-gray-300'}`}>
					{isComplete ? 'Arquivo salvo com sucesso!' : 'Salvando arquivo...'}
				</p>
			</div>

			{/* Steps */}
			<ul className="space-y-2">
				{steps.map((s, idx) => {
					const isDone = currentIndex > idx || (s.key === 'done' && current === 'done');
					const isCurrent = currentIndex === idx && s.key !== 'done';
					return (
						<li key={s.key} className="flex items-center gap-3 text-sm">
							{/* Emoji ou indicador */}
							<span className="text-lg flex-shrink-0 w-6 flex items-center justify-center">
								{isDone ? (
									<span className="animate-bounce">{s.emoji}</span>
								) : isCurrent ? (
									<Loader2 className="w-4 h-4 text-blue-400 animate-spin" />
								) : (
									<span className="opacity-30">{s.emoji}</span>
								)}
							</span>
							{/* Label */}
							<span className={`font-medium transition-all duration-200 ${
								isDone ? 'text-green-300 scale-105' : isCurrent ? 'text-blue-300' : 'text-gray-500'
							}`}>
								{s.label}
							</span>
							{/* Checkmark ao finalizar */}
							{isDone && (
								<CheckCircle2 className="w-4 h-4 text-green-400 ml-auto animate-scale-in" />
							)}
						</li>
					);
				})}
			</ul>

			{/* Mensagem de sucesso adicional */}
			{isComplete && (
				<div className="mt-4 p-2 bg-green-800/30 border border-green-500/30 rounded-md">
					<p className="text-xs text-green-200 text-center">
						Arquivo registrado no sistema e pronto para uso
					</p>
				</div>
			)}
		</div>
	);
}

export default SaveProgress;


